/**
 * BeHeard's NONCOMMERCIAL session capacities per capacity tier (product
 * safety, not prices). Which tier applies is decided only by
 * services/plan/capacityTier.ts; commercial plans/entitlements are Central's
 * (docs/PLAN-AUTHORITY.md).
 *
 * Plan Policies Configuration
 *
 * Defines numeric limits for each plan tier.
 * These limits are separate from credits - they define what a plan allows,
 * while credits track consumption of allowed actions.
 *
 * Plan Limits = "You're allowed to do this type of thing"
 * Credits = "You have enough fuel to do it right now"
 */

import { limitLabels } from './limitCopy.js';

export interface PlanLimits {
  // Practice session limits
  maxDailyPracticeSessions?: number;
  maxMonthlyPracticeSessions?: number;

  // AI voice limits (in minutes)
  maxDailyAiVoiceMinutes?: number;
  maxMonthlyAiVoiceMinutes?: number;

  // NPC battles
  maxDailyNpcBattles?: number;
  maxMonthlyNpcBattles?: number;

  // Content sets (user's own content library)
  maxContentSets?: number;
  maxContentItemsPerSet?: number;

  // Blueprints (story prep)
  maxBlueprints?: number;
  maxPetals?: number;

  // Portfolio
  maxPortfolioItems?: number;

  // Coaching (as a client receiving coaching)
  maxMonthlyCoachingSessions?: number;

  // Marketplace visibility
  canBeListedOnMarketplace?: boolean;

  // Analytics access level
  analyticsAccess?: 'none' | 'basic' | 'advanced';

  // RAG / Custom knowledge base
  maxRagDocuments?: number;
  maxRagStorageMb?: number;
}

// Free tier - Safe and contained but still fun
const FREE: PlanLimits = {
  maxDailyPracticeSessions: 3,
  maxMonthlyPracticeSessions: 60,
  maxDailyAiVoiceMinutes: 0, // No AI voice on free
  maxMonthlyAiVoiceMinutes: 0,
  maxDailyNpcBattles: 5,
  maxMonthlyNpcBattles: 100,
  maxContentSets: 0, // No content library on free
  maxContentItemsPerSet: 0,
  maxBlueprints: 3,
  maxPetals: 20,
  maxPortfolioItems: 3,
  maxMonthlyCoachingSessions: 0,
  canBeListedOnMarketplace: false,
  analyticsAccess: 'none',
  maxRagDocuments: 0,
  maxRagStorageMb: 0,
};

// Pro Individual - Full individual features
const PRO_INDIVIDUAL: PlanLimits = {
  maxDailyPracticeSessions: 20,
  maxMonthlyPracticeSessions: 600,
  maxDailyAiVoiceMinutes: 30,
  maxMonthlyAiVoiceMinutes: 300,
  maxDailyNpcBattles: 50,
  maxMonthlyNpcBattles: 1000,
  maxContentSets: 10,
  maxContentItemsPerSet: 100,
  maxBlueprints: 50,
  maxPetals: 500,
  maxPortfolioItems: 50,
  maxMonthlyCoachingSessions: 10,
  canBeListedOnMarketplace: true,
  analyticsAccess: 'advanced',
  maxRagDocuments: 20,
  maxRagStorageMb: 100,
};

/**
 * Plan policies keyed by plan key.
 * These define the limits for each subscription plan.
 *
 * Plan-key mapping. ONE resolver (services/plan/capacityTier.ts, see
 * docs/PLAN-AUTHORITY.md) decides a user's capacity tier; both enforcers read
 * that same tier:
 *
 *   Tier       PLAN_POLICIES key (PlanLimitService)   PLAN_CAPABILITIES key (UsageService)
 *   Free       free                                    free
 *   Pro        pro_individual                          pro
 *   Pro + AI   pro_ai                                  pro_ai
 *
 * PlanLimitService reads SESSION limits from this table by the tier's
 * PLAN_POLICY_KEY_BY_TIER key; UsageService reads credit / provider-cost
 * capabilities from types/usage.ts PLAN_CAPABILITIES by the tier itself.
 *
 * Pro + AI (owner decision): its session-count limits are Pro's. The "+ AI"
 * distinction lives only in the credit / provider-cost model
 * (PLAN_CAPABILITIES: unlimitedPractice, canUseAI, ...), never in session
 * counts. `pro_ai` is derived from PRO_INDIVIDUAL — not copied numbers — so the
 * two cannot drift, and it overrides nothing.
 */
export const PLAN_POLICIES: Record<string, PlanLimits> = {
  free: FREE,
  pro_individual: PRO_INDIVIDUAL,
  pro_ai: { ...PRO_INDIVIDUAL },
};

/** User.planTier values (models/User.ts PlanTier; PLAN_CAPABILITIES planKey). */
export type UserPlanTier = 'free' | 'pro' | 'pro_ai';

/** User.planTier → the PLAN_POLICIES key with that plan's limits (see the mapping above). */
export const PLAN_POLICY_KEY_BY_TIER: Record<UserPlanTier, string> = {
  free: 'free',
  pro: 'pro_individual',
  pro_ai: 'pro_ai',
};

/**
 * Add-on policies that stack with base plan.
 * These provide additional capabilities beyond the base plan.
 */
export interface AddOnLimits {
  // Coach add-on
  maxCoachingClients?: number;
  maxActiveEngagements?: number;

  // Recruiter add-on
  maxSavedCandidates?: number;
  maxMonthlyContactRequests?: number;
  maxEvaluations?: number;
}

export const ADDON_POLICIES: Record<string, AddOnLimits> = {
  coach_addon: {
    maxCoachingClients: 10,
    maxActiveEngagements: 5,
  },
  coach_pro_addon: {
    maxCoachingClients: 50,
    maxActiveEngagements: 25,
  },
  recruiter_addon: {
    maxSavedCandidates: 100,
    maxMonthlyContactRequests: 20,
    maxEvaluations: 50,
  },
};

const warnedUnknownPlanKeys = new Set<string>();

/**
 * Get plan limits for a given plan key.
 * Falls back to the Free tier if the plan is not found — an unknown key never
 * gets Pro limits. Each unknown key is warned about once per process (console,
 * not the logger: this file is also compiled into the browser bundle).
 */
export function getPlanLimits(planKey: string | null | undefined): PlanLimits {
  if (!planKey) return PLAN_POLICIES.free;
  if (Object.prototype.hasOwnProperty.call(PLAN_POLICIES, planKey)) return PLAN_POLICIES[planKey];
  if (!warnedUnknownPlanKeys.has(planKey)) {
    warnedUnknownPlanKeys.add(planKey);
    console.warn(`[planPolicies] Unknown plan key "${planKey}": applying Free plan limits`);
  }
  return PLAN_POLICIES.free;
}

/**
 * Get add-on limits for a given add-on key.
 */
export function getAddOnLimits(addOnKey: string): AddOnLimits | undefined {
  return ADDON_POLICIES[addOnKey];
}

/**
 * Human-readable limit names, keyed by limit key. Derived from the one limit
 * copy (config/limitCopy.ts) so labels, error messages and the web's copy agree.
 */
export const LIMIT_KEY_LABELS: Record<string, string> = limitLabels('user');
